import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EntreeJournalAudit } from '@genviz/shared/dto';

import { useAudit, useExploitations } from '../api/queries';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import { Badge } from '../components/Badges';
import { Bouton } from '../components/Boutons';
import { Carte } from '../components/Carte';
import { Saisie, Selecteur } from '../components/Champs';
import { JsonBrut } from '../components/JsonBrut';
import { MessageErreur } from '../components/MessageErreur';
import { Modale } from '../components/Modale';
import { useNotifications } from '../components/Notifications';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_ACTEUR_AUDIT, LIBELLE_ACTION_AUDIT, libelleAction, VALEUR_ABSENTE } from '../i18n/fr';
import { construireCsv } from '../lib/csv';
import { horodatageFichier, telechargerTexte } from '../lib/divers';
import { formaterDateHeureSecondes, formaterNombre, versIsoUtc } from '../lib/format';

const OPTIONS_ACTION = Object.keys(LIBELLE_ACTION_AUDIT)
  .map((code) => ({ valeur: code, libelle: libelleAction(code) }))
  .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));

const TON_ACTEUR: Record<EntreeJournalAudit['acteur_type'], 'neutre' | 'bleu' | 'orange' | 'noir'> = {
  UTILISATEUR: 'bleu',
  OBJET: 'neutre',
  BACKOFFICE: 'orange',
  SYSTEME: 'noir',
};

/**
 * Journal d'audit.
 *
 * C'est la pièce que l'on produit quand un client affirme n'avoir jamais éteint sa clôture. Il
 * est donc consulté en LECTURE SEULE, filtrable par exploitation, action et période, et
 * exportable — un ticket ou un litige se règle avec un fichier joint, pas avec une capture
 * d'écran. L'export ne porte que sur ce qui est chargé : ce qu'on exporte est exactement ce
 * qu'on a vu.
 */
export function AuditPage(): JSX.Element {
  const notifications = useNotifications();
  const [parametres, setParametres] = useSearchParams();
  const [detail, setDetail] = useState<EntreeJournalAudit | null>(null);

  const exploitationId = parametres.get('exploitation_id') ?? '';
  const action = parametres.get('action') ?? '';
  const depuis = parametres.get('depuis') ?? '';
  const jusquA = parametres.get('jusqu_a') ?? '';

  // Les bornes viennent de l'URL : elles peuvent être tronquées ou retapées à la main. Une
  // valeur illisible est ignorée (`versIsoUtc` renvoie null) au lieu de faire tomber l'écran.
  const filtres = useMemo(() => {
    const depuisIso = versIsoUtc(depuis);
    const jusquAIso = versIsoUtc(jusquA);
    return {
      ...(exploitationId ? { exploitation_id: exploitationId } : {}),
      ...(action ? { action } : {}),
      ...(depuisIso ? { depuis: depuisIso } : {}),
      ...(jusquAIso ? { jusqu_a: jusquAIso } : {}),
    };
  }, [exploitationId, action, depuis, jusquA]);

  const borneInvalide =
    (depuis !== '' && versIsoUtc(depuis) === null) || (jusquA !== '' && versIsoUtc(jusquA) === null);

  const journal = useAudit(filtres);
  // Le journal nomme les exploitations : on ne charge la liste que si l'opérateur a le droit de
  // la voir, plutôt que de provoquer un 403 pour remplir une colonne.
  const peutVoirClients = usePeut(Capacite.CONSULTER_EXPLOITATIONS);
  const exploitations = useExploitations({}, { actif: peutVoirClients });

  const entrees = useMemo(
    () => journal.data?.pages.flatMap((p) => p.items) ?? [],
    [journal.data],
  );

  const majFiltre = (cle: string, valeur: string): void => {
    const suivants = new URLSearchParams(parametres);
    if (valeur === '') suivants.delete(cle);
    else suivants.set(cle, valeur);
    setParametres(suivants, { replace: true });
  };

  const nomExploitation = (id: string | null): string => {
    if (!id) return VALEUR_ABSENTE;
    return exploitations.data?.items.find((e) => e.id === id)?.nom ?? id;
  };

  const exporter = (): void => {
    const csv = construireCsv(
      ['at', 'acteur_type', 'acteur_id', 'exploitation', 'action', 'cible_type', 'cible_id', 'ip'],
      entrees.map((e) => [
        e.at,
        e.acteur_type,
        e.acteur_id,
        nomExploitation(e.exploitation_id),
        e.action,
        e.cible_type,
        e.cible_id,
        e.ip,
      ]),
    );
    telechargerTexte(`genviz-audit-${horodatageFichier()}.csv`, csv);
    notifications.succes(`${entrees.length} entrée(s) exportée(s).`);
  };

  const colonnes: Array<Colonne<EntreeJournalAudit>> = [
    {
      cle: 'at',
      entete: 'Horodatage',
      rendu: (e) => <span className="gv-mono gv-corps-s">{formaterDateHeureSecondes(e.at)}</span>,
    },
    {
      cle: 'acteur',
      entete: 'Acteur',
      rendu: (e) => <Badge ton={TON_ACTEUR[e.acteur_type]} libelle={LIBELLE_ACTEUR_AUDIT[e.acteur_type]} />,
    },
    { cle: 'action', entete: 'Action', rendu: (e) => libelleAction(e.action) },
    {
      cle: 'exploitation',
      entete: 'Exploitation',
      rendu: (e) => nomExploitation(e.exploitation_id),
    },
    {
      cle: 'cible',
      entete: 'Cible',
      rendu: (e) =>
        e.cible_type ? (
          <span className="gv-mono gv-corps-s">
            {e.cible_type}
            {e.cible_id ? ` · ${e.cible_id.slice(0, 8)}…` : ''}
          </span>
        ) : (
          VALEUR_ABSENTE
        ),
    },
    { cle: 'ip', entete: 'IP', rendu: (e) => <span className="gv-mono gv-corps-s">{e.ip ?? VALEUR_ABSENTE}</span> },
    {
      cle: 'diff',
      entete: '',
      rendu: (e) =>
        e.avant || e.apres ? (
          <Bouton
            petit
            variante="discret"
            onClick={(evt) => {
              evt.stopPropagation();
              setDetail(e);
            }}
          >
            Avant / après
          </Bouton>
        ) : null,
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Journal d’audit</h1>
          <span className="gv-corps-s gv-secondaire">
            Rétention 12 mois · {formaterNombre(entrees.length)} entrée(s) chargée(s)
          </span>
        </div>
        <div className="gv-entete__actions">
          <Bouton onClick={exporter} disabled={entrees.length === 0}>
            Exporter le CSV
          </Bouton>
        </div>
      </header>

      <Carte>
        <div className="gv-filtres">
          <Selecteur
            label="Exploitation"
            optionVide="Toutes"
            options={(exploitations.data?.items ?? []).map((e) => ({ valeur: e.id, libelle: e.nom }))}
            value={exploitationId}
            onChange={(e) => {
              majFiltre('exploitation_id', e.currentTarget.value);
            }}
          />
          <Selecteur
            label="Action"
            optionVide="Toutes les actions"
            options={OPTIONS_ACTION}
            value={action}
            onChange={(e) => {
              majFiltre('action', e.currentTarget.value);
            }}
          />
          <Saisie
            label="Depuis"
            type="datetime-local"
            value={depuis}
            onChange={(e) => {
              majFiltre('depuis', e.currentTarget.value);
            }}
          />
          <Saisie
            label="Jusqu’à"
            type="datetime-local"
            value={jusquA}
            onChange={(e) => {
              majFiltre('jusqu_a', e.currentTarget.value);
            }}
          />
        </div>
      </Carte>

      {borneInvalide ? (
        <div className="gv-bandeau gv-bandeau--attention">
          Une borne de date de l’adresse est illisible : elle a été ignorée. Ressaisissez-la
          ci-dessus si le filtre doit s’appliquer.
        </div>
      ) : null}

      {journal.error ? <MessageErreur erreur={journal.error} /> : null}

      <Carte sansPadding>
        <Tableau
          colonnes={colonnes}
          lignes={entrees}
          cleDe={(e) => String(e.id)}
          chargement={journal.isPending}
          titreVide="Aucune entrée"
          descriptionVide="Aucune action ne correspond à ces filtres sur la période demandée."
          pied={
            journal.hasNextPage ? (
              <Bouton
                petit
                occupe={journal.isFetchingNextPage}
                onClick={() => {
                  void journal.fetchNextPage();
                }}
              >
                Charger la suite
              </Bouton>
            ) : (
              <span>Fin du journal pour ces filtres.</span>
            )
          }
        />
      </Carte>

      <Modale
        titre={detail ? `${libelleAction(detail.action)} — ${formaterDateHeureSecondes(detail.at)}` : ''}
        ouverte={detail !== null}
        surFermeture={() => {
          setDetail(null);
        }}
        large
      >
        {detail ? (
          <div className="gv-grille gv-grille--2">
            <div className="gv-pile gv-pile--serree">
              <span className="gv-label">Avant</span>
              {detail.avant ? <JsonBrut valeur={detail.avant} /> : <p className="gv-secondaire">—</p>}
            </div>
            <div className="gv-pile gv-pile--serree">
              <span className="gv-label">Après</span>
              {detail.apres ? <JsonBrut valeur={detail.apres} /> : <p className="gv-secondaire">—</p>}
            </div>
          </div>
        ) : null}
      </Modale>
    </>
  );
}
